import React from 'react';
import { Box, Button, EmptyState, Flex, Text, TextField, LoadingOrbit, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import {
  CaretDown,
  CaretRight,
  MagnifyingGlass,
  Funnel,
  Warning,
  Copy,
  CheckCircle,
  Clock,
  GlobeSimple,
  Link as LinkIcon,
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { NetworkRequestEntry } from '@chatsdet/types';
import { BodyViewer } from './BodyViewer';
import { useIssueNetworkRequests } from '../../../../hooks/useIssuesV2';

type NetworkErrorsTabProps = {
  /** Issue ID for fetching network requests */
  issueId: string;
  /** Occurrence ID for fetching network requests */
  occurrenceId: string | null;
  /** Legacy: single failure URL (fallback) */
  networkFailureUrl?: string | null;
  /** Legacy: single failure status (fallback) */
  networkFailureStatus?: number | null;
};

type FilterMode = 'errors' | 'all';

function getStatusBadgeVariant(
  status: number | null
): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (status === null) return 'secondary';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'warning';
  return 'destructive';
}

function isErrorStatus(status: number | null): boolean {
  if (status === null) return true; // Pending/failed requests are considered errors
  return status >= 400;
}

// Collapsible section wrapper for detail panels
function CollapsibleSection({
  title,
  count,
  isExpanded,
  onToggle,
  children,
  copyContent,
}: {
  title: string;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  copyContent?: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!copyContent) return;
    await navigator.clipboard.writeText(copyContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <Flex align="center" gap="sm">
          <span className="text-muted-foreground">
            {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
          </span>
          <Text size="sm" className="font-medium">
            {title}
          </Text>
          {count !== undefined && (
            <TagBadge name={count} color="gray" hideDot size="sm" className="text-[10px]" />
          )}
        </Flex>
        {copyContent && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="px-2 opacity-60 hover:opacity-100"
          >
            {copied ? <CheckCircle size={14} className="text-success" /> : <Copy size={14} />}
          </Button>
        )}
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-border bg-muted/20">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Individual header row with copy on hover
function HeaderRow({ name, value }: { name: string; value: string }) {
  const [copied, setCopied] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="group flex gap-4 py-2 px-3 hover:bg-muted/30 rounded transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Text
        size="xs"
        className="font-mono text-muted-foreground shrink-0 w-36 truncate"
        title={name}
      >
        {name}
      </Text>
      <Text size="xs" className="font-mono flex-1 break-all">
        {value}
      </Text>
      {isHovered && (
        <button
          onClick={handleCopy}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          title="Copy value"
        >
          {copied ? <CheckCircle size={12} className="text-success" /> : <Copy size={12} />}
        </button>
      )}
    </div>
  );
}

// Headers table as collapsible section
function CollapsibleHeadersSection({
  title,
  headers,
  defaultExpanded = false,
}: {
  title: string;
  headers: Record<string, string> | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const entries = headers ? Object.entries(headers) : [];
  const count = entries.length;

  if (count === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <Flex align="center" gap="sm">
          <Text size="sm" className="font-medium text-muted-foreground">
            {title}
          </Text>
          <TagBadge name="0" color="gray" hideDot size="sm" className="text-[10px]" />
        </Flex>
      </div>
    );
  }

  const copyContent = entries.map(([k, v]) => `${k}: ${v}`).join('\n');

  return (
    <CollapsibleSection
      title={title}
      count={count}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      copyContent={copyContent}
    >
      <div className="pt-3 space-y-0.5">
        {entries.map(([key, value]) => (
          <HeaderRow key={key} name={key} value={value} />
        ))}
      </div>
    </CollapsibleSection>
  );
}

// Hero section and detail view for an expanded request
function NetworkRequestDetail({ request }: { request: NetworkRequestEntry }) {
  const [urlCopied, setUrlCopied] = React.useState(false);

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(request.url);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const isError = isErrorStatus(request.status);

  return (
    <div className="border-t border-border bg-muted/20 p-4 space-y-4">
      {/* Hero Section */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        {/* Status + Method + Duration row */}
        <Flex align="center" gap="md" wrap>
          <TagBadge name={request.status ?? 'Pending'} color={badgeVariantToTagColor(getStatusBadgeVariant(request.status))} hideDot size="lg" className="text-base font-bold px-3 py-1" />
          <TagBadge name={request.method} color={badgeVariantToTagColor(request.method === 'GET' ? 'secondary' : 'outline')} hideDot size="sm" className="font-mono" />
          {request.durationMs !== null && (
            <Flex align="center" gap="xs" className="text-muted-foreground">
              <Clock size={14} />
              <Text size="sm">{request.durationMs}ms</Text>
            </Flex>
          )}
          {request.contentType && (
            <Text size="xs" className="text-muted-foreground font-mono">
              {request.contentType}
            </Text>
          )}
        </Flex>

        {/* Full URL */}
        <Flex align="start" gap="sm" className="group">
          <LinkIcon size={16} className="text-muted-foreground shrink-0 mt-0.5" />
          <a
            href={request.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline break-all font-mono flex-1"
          >
            {request.url}
          </a>
          <button
            onClick={handleCopyUrl}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100"
            title="Copy URL"
          >
            {urlCopied ? <CheckCircle size={16} className="text-success" /> : <Copy size={16} />}
          </button>
        </Flex>
      </div>

      {/* Collapsible Sections - Stacked Vertically */}
      <div className="space-y-3">
        <CollapsibleHeadersSection
          title="Request Headers"
          headers={request.requestHeaders}
          defaultExpanded={false}
        />

        <CollapsibleHeadersSection
          title="Response Headers"
          headers={request.responseHeaders}
          defaultExpanded={false}
        />

        {/* Request Body */}
        <CollapsibleBodySection
          title="Request Body"
          body={request.requestBody}
          defaultExpanded={false}
        />

        {/* Response Body - expanded by default for errors */}
        <CollapsibleBodySection
          title="Response Body"
          body={request.responseBody}
          defaultExpanded={isError && Boolean(request.responseBody)}
        />
      </div>
    </div>
  );
}

// Collapsible body section wrapper
function CollapsibleBodySection({
  title,
  body,
  defaultExpanded = false,
}: {
  title: string;
  body: string | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  if (!body) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <Flex align="center" gap="sm">
          <Text size="sm" className="font-medium text-muted-foreground">
            {title}
          </Text>
          <Text size="xs" className="text-muted-foreground italic">
            No body
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <CollapsibleSection
      title={title}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      copyContent={body}
    >
      <div className="pt-3">
        <BodyViewer body={body} maxCollapsedHeight={300} />
      </div>
    </CollapsibleSection>
  );
}

function NetworkRequestRow({
  request,
  isExpanded,
  onToggle,
}: {
  request: NetworkRequestEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const urlObj = (() => {
    try {
      return new URL(request.url);
    } catch {
      return null;
    }
  })();

  const displayUrl = urlObj ? `${urlObj.pathname}${urlObj.search}` : request.url;
  const hostname = urlObj?.hostname ?? '';

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
      >
        <span className="text-muted-foreground">
          {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </span>
        <TagBadge name={request.method} color={badgeVariantToTagColor(request.method === 'GET' ? 'secondary' : 'outline')} hideDot size="sm" className="w-14 justify-center font-mono text-[10px]" />
        <TagBadge name={request.status ?? '—'} color={badgeVariantToTagColor(getStatusBadgeVariant(request.status))} hideDot size="sm" className="w-12 justify-center" />
        <Flex direction="column" className="flex-1 min-w-0">
          <Text size="xs" className="font-mono truncate">
            {displayUrl}
          </Text>
          {hostname && (
            <Text size="xs" className="text-muted-foreground truncate">
              {hostname}
            </Text>
          )}
        </Flex>
        {request.durationMs !== null && (
          <Text size="xs" className="text-muted-foreground whitespace-nowrap">
            {request.durationMs}ms
          </Text>
        )}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <NetworkRequestDetail request={request} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LegacyNetworkError({
  networkFailureUrl,
  networkFailureStatus,
}: {
  networkFailureUrl: string | null;
  networkFailureStatus: number | null;
}) {
  return (
    <Box className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <Flex direction="column" gap="xs" align="start">
        <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
          Status
        </Text>
        <TagBadge name={networkFailureStatus ?? 'Unknown'} color={badgeVariantToTagColor(getStatusBadgeVariant(networkFailureStatus))} hideDot size="sm" />
      </Flex>
      {networkFailureUrl ? (
        <Flex direction="column" gap="xs">
          <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
            URL
          </Text>
          <a
            href={networkFailureUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline break-all"
          >
            {networkFailureUrl}
          </a>
        </Flex>
      ) : null}
    </Box>
  );
}

export function NetworkErrorsTab({
  issueId,
  occurrenceId,
  networkFailureUrl,
  networkFailureStatus,
}: NetworkErrorsTabProps) {
  const [filterMode, setFilterMode] = React.useState<FilterMode>('errors');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());

  // Fetch network requests lazily from S3
  const networkRequestsQuery = useIssueNetworkRequests(issueId, occurrenceId);
  const networkRequests = networkRequestsQuery.data ?? null;

  // Loading state
  if (networkRequestsQuery.isLoading) {
    return (
      <div className="px-6 py-6">
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="border border-dashed border-border rounded-lg py-10 space-y-2"
        >
          <LoadingOrbit size="md" />
          <Text size="sm" className="text-muted-foreground">
            Loading network requests...
          </Text>
        </Flex>
      </div>
    );
  }

  // Error state
  if (networkRequestsQuery.isError) {
    return (
      <div className="px-6 py-6">
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="border border-dashed border-border rounded-lg py-10 space-y-2"
        >
          <Warning size={24} className="text-destructive" />
          <Text size="sm" className="text-muted-foreground">
            Failed to load network requests.
          </Text>
        </Flex>
      </div>
    );
  }

  // If we have structured data, use the enhanced view
  const hasStructuredData = networkRequests && networkRequests.length > 0;

  if (!hasStructuredData) {
    // Fall back to legacy view
    if (!networkFailureUrl && networkFailureStatus === null) {
      return (
        <div className="px-6 py-6">
          <EmptyState
            size="sm"
            icon={<GlobeSimple size={22} />}
            title="No network activity"
            description="No network requests were captured for this occurrence."
          />
        </div>
      );
    }

    return (
      <div className="px-6 py-6">
        <LegacyNetworkError
          networkFailureUrl={networkFailureUrl ?? null}
          networkFailureStatus={networkFailureStatus ?? null}
        />
      </div>
    );
  }

  // Filter requests
  const filteredRequests = networkRequests.filter((req) => {
    // Apply error filter
    if (filterMode === 'errors' && !isErrorStatus(req.status)) {
      return false;
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return req.url.toLowerCase().includes(query);
    }
    return true;
  });

  const errorCount = networkRequests.filter((r) => isErrorStatus(r.status)).length;
  const totalCount = networkRequests.length;

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="px-6 py-6 space-y-3">
      {/* Toolbar */}
      <Flex align="center" gap="sm" wrap>
        <Button
          variant={filterMode === 'errors' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilterMode('errors')}
          iconLeft={<Funnel size={14} />}
        >
          Errors only ({errorCount})
        </Button>
        <Button
          variant={filterMode === 'all' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilterMode('all')}
        >
          All requests ({totalCount})
        </Button>
        <div className="flex-1 min-w-48 max-w-xs">
          <TextField
            placeholder="Search URLs..."
            value={searchQuery}
            onChange={setSearchQuery}
            leftIcon={<MagnifyingGlass size={14} />}
          />
        </div>
      </Flex>

      {/* Results */}
      {filteredRequests.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<GlobeSimple size={22} />}
          title={searchQuery ? 'No matching requests' : 'No requests to display'}
          description={searchQuery ? 'Try a different search or filter.' : undefined}
        />
      ) : (
        <Box className="rounded-lg border border-border overflow-hidden">
          {filteredRequests.map((request, index) => (
            <NetworkRequestRow
              key={index}
              request={request}
              isExpanded={expandedRows.has(index)}
              onToggle={() => toggleRow(index)}
            />
          ))}
        </Box>
      )}
    </div>
  );
}
